// AnalyticsQuery.jsx
import { useEffect, useCallback, useState, useRef } from 'react'
import Table from 'AnalyticsDashboard/components/Table'
import TimeSeriesTable from './TimeSeriesTable'
import LineChart from 'AnalyticsDashboard/components/LineChart'
import LineChartV2 from 'AnalyticsDashboard/components/LineChartV2'
import PieChart from 'AnalyticsDashboard/components/PieChart'
import BarChart from 'AnalyticsDashboard/components/BarChart'
import GaugeChart from 'AnalyticsDashboard/components/GaugeChart'
import MapChart from 'AnalyticsDashboard/components/MapChart'
import NumberChart from 'AnalyticsDashboard/components/NumberChart'
import ConfidenceIntervalLineChart from 'AnalyticsDashboard/components/ConfidenceIntervalLineChart'
import prepareChartData from 'Analytics/utils/prepareChartData'
import Loading from 'Shared/components/Loading'
import { useAnalyticsDashboard, useAnalyticsQuery, useAnalyticsContext } from 'AnalyticsDashboard/AnalyticsContext'

// Chart type display labels
const CHART_TYPE_LABELS = {
  table: 'Table',
  line: 'Line Chart',
  lineV2: 'Line Chart',
  pie: 'Pie Chart',
  bar: 'Bar Chart',
  map: 'Map (UK)',
  mapWorld: 'Map (World)',
  timeSeriesTable: 'Time Series Table',
  number: 'Number',
  gauge: 'Gauge',
  confidenceIntervalLineChart: 'Confidence Interval',
  json: 'JSON'
}

export default function AnalyticsQuery({
  kind,
  chartType = 'line', // Default chart type
  supportedChartTypes = [], // Array of supported chart types for toggling
  chartOptions,
  controlOptions = { showChartType: false, showTimeBucket: true }, // Whether to show view options
  metrics = [{ id: 'count' }], // Default metrics to show
  dimensions = [],
  primaryDimension = { id: 'time', label: 'Time' },
  secondaryDimension,
  parameters = {},
  global = false,
  orderBy = [],
  limit = null, // Optional limit for the number of results
  overrideFilters = {},
  chartComponent = null // Optional custom chart component
}) {
  const { registerQuery, unregisterQuery } = useAnalyticsDashboard()

  const idRef = useRef(null)
  if (!idRef.current) idRef.current = _.uniqueId('aq_')
  const id = idRef.current

  // Sync all props to context after each render (deferred to avoid
  // "Cannot update a component while rendering" warning)
  useEffect(() => {
    registerQuery({
      id,
      kind,
      chartType,
      supportedChartTypes,
      chartOptions,
      controlOptions,
      dimensions,
      primaryDimension,
      secondaryDimension,
      metrics,
      parameters,
      global,
      orderBy,
      limit,
      overrideFilters,
      chartComponent
    })

    return () => unregisterQuery(id)
  }, [])

  return <AnalyticsQueryInner id={id} />
}

function AnalyticsQueryInner({ id }) {
  const {
    query,
    updateQuery,
    updateDimension
  } = useAnalyticsQuery(id)
  const { state: { uiEnabled} } = useAnalyticsContext()
  const { fetchData } = useAnalyticsDashboard()

  // Handle fetching data if query changes
  const queryExists = !!query
  const timeBucket = query?.dimensions?.find(d => d.id === 'time')?.timeBucket || null
  useEffect(() => {
    if (!queryExists) return

    fetchData(id)
  }, [queryExists, timeBucket])

  // Tab state - initialized after we have chartOptions
  const [activeTabId, setActiveTabId] = useState(null)

  console.log({ query })
  if (!query || query.dataLoading) {
    return <div>Loading...</div>
  }

  const { data: returnedData, dataLoading, chartType, supportedChartTypes = [], controlOptions, metrics: queryMetrics, chartOptions = {}, dimensions, chartComponent } = query
  const { showChartType, showTimeBucket } = controlOptions || {}
  const showChartTypeToggle = supportedChartTypes.length > 1
  const { data, dimensionInfo } = returnedData || {}

  // Handle tabs
  const tabs = chartOptions.tabs || []
  const currentTabId = activeTabId || (tabs.length > 0 ? tabs[0].id : null)
  const activeTab = tabs.find(t => t.id === currentTabId)

  // Merge active tab overrides into chartOptions
  const effectiveChartOptions = activeTab
    ? {
        ...chartOptions,
        y: activeTab.y !== undefined ? activeTab.y : chartOptions.y,
        seriesBy: activeTab.seriesBy !== undefined ? activeTab.seriesBy : chartOptions.seriesBy
      }
    : chartOptions

  console.log({ query })
  const primaryDimension = { ...query.primaryDimension, id: _.camelCase(query.primaryDimension.id) }
  const secondaryDimension = query.secondaryDimension ? { ...query.secondaryDimension, id: _.camelCase(query.secondaryDimension.id) } : null
  const metrics = queryMetrics.map(metric => ({ ...metric, id: _.camelCase(metric.id) }))
  const chartData = data ? prepareChartData({ chartType, data, metrics, primaryDimension, secondaryDimension }) : null
  console.log('Chart data:', id, chartData)
  const showData = data && data.length > 0 && !dataLoading
  const setChartType = (chartType) => updateQuery({ chartType })

  const chartProps = { data: chartData, chartOptions: effectiveChartOptions, dimensionInfo }
  const rawChartProps = { data, chartOptions: effectiveChartOptions, dimensionInfo, query }

  return (
    <div className='analytics-query'>
      {showChartTypeToggle && (
        <div className='chart-type-toggle'>
          <select value={chartType} onChange={(e) => setChartType(e.target.value)}>
            {supportedChartTypes.map(type => (
              <option key={type} value={type}>{CHART_TYPE_LABELS[type] || type}</option>
            ))}
          </select>
        </div>
      )}
      {uiEnabled && showChartType && !showChartTypeToggle && (
        <div>
          <select value={chartType} onChange={(e) => setChartType(e.target.value)}>
            <option value="table">Table</option>
            <option value="line">Line Chart</option>
            <option value="pie">Pie Chart</option>
            <option value="bar">Bar Chart</option>
            <option value="map">Map UK</option>
            <option value="mapWorld">Map World</option>
          </select>
        </div>
      )}
      {uiEnabled && showTimeBucket && timeBucket && (
        <div>
          <label>Time Bucket: </label>
          <select value={timeBucket} onChange={(e) => updateDimension('time', { timeBucket: e.target.value })}>
            <option value="hour">Hour</option>
            <option value="day">Day</option>
            <option value="week">Week</option>
            <option value="month">Month</option>
            <option value="quarter">Quarter</option>
            <option value="year">Year</option>
          </select>
        </div>
      )}
      {tabs.length > 1 && (
        <div className="chart-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              type="button"
              className={`chart-tab ${currentTabId === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTabId(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}
      {dataLoading && <Loading />}
      {!showData && !dataLoading && <p>No data available for the selected filters and segments.</p>}
      {showData && chartType === 'line' && <LineChart {...chartProps} />}
      {showData && chartType === 'lineV2' && <LineChartV2 {...rawChartProps} />}
      {showData && chartType === 'pie' && <PieChart {...chartProps} />}
      {showData && chartType === 'bar' && <BarChart {...chartProps} />}
      {showData && chartType === 'map' && <MapChart {...chartProps} mapId='uk-wpc' />}
      {showData && chartType === 'mapWorld' && <MapChart {...chartProps} mapId='world' />}
      {showData && chartType === 'table' && <Table {...rawChartProps} />}
      {showData && chartType === 'timeSeriesTable' && <TimeSeriesTable {...rawChartProps} />}
      {showData && chartType === 'number' && <NumberChart {...rawChartProps} />}
      {showData && chartType === 'gauge' && <GaugeChart {...rawChartProps} />}
      {showData && chartType === 'confidenceIntervalLineChart' && <ConfidenceIntervalLineChart {...rawChartProps} />}
      {showData && chartType === 'json' && <div>{ JSON.stringify(data, null, 2) }</div>}
      {showData && chartComponent && React.createElement(chartComponent, { data, chartOptions, query })}
    </div>
  )
}

